import { useEffect, useMemo, useRef, useState } from 'react';
import type { Client } from '@/lib/types';

interface ClientPickerProps {
  clients: Client[];
  customer: Client | null;
  onChange: (client: Client | null) => void;
  onAdd: () => void;
  autoFocus?: boolean;
  onDismiss?: () => void;
}

/**
 * Campo "Cliente / Mayorista": búsqueda por nombre, teléfono, RNC o código mayorista.
 * Muestra información del cliente mayorista cuando está seleccionado.
 */
export default function ClientPicker({
  clients,
  customer,
  onChange,
  onAdd,
  autoFocus,
  onDismiss,
}: ClientPickerProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients.slice(0, 12);
    return clients
      .filter((c) => {
        const hay =
          c.name.toLowerCase().includes(q) ||
          (c.phone ?? '').toLowerCase().includes(q) ||
          (c.rnc ?? '').toLowerCase().includes(q) ||
          (c.wholesale_code ?? '').toLowerCase().includes(q) ||
          (c.company ?? '').toLowerCase().includes(q);
        return hay;
      })
      .slice(0, 12);
  }, [clients, query]);

  const initials = customer?.name?.trim().charAt(0).toUpperCase();
  const isWholesale = !!customer?.is_wholesale;
  const isWholesaleActive = isWholesale && customer?.active;

  const select = (c: Client) => {
    onChange(c);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="space-y-2">
      <div ref={rootRef} className="relative flex items-center gap-2">
        <span className="w-10 h-10 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center shrink-0 border border-background-200/70 overflow-hidden">
          {initials ? (
            <span className="text-sm font-semibold">{initials}</span>
          ) : (
            <i className="ri-user-3-line text-lg" />
          )}
        </span>

        <div className="relative flex-1 min-w-0">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none" />
          <input
            autoFocus={autoFocus}
            value={customer ? query : query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
              if (!e.target.value && customer) onChange(null);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && matches.length === 1) {
                e.preventDefault();
                select(matches[0]);
              }
            }}
            placeholder={customer ? customer.name : 'Cliente / Mayorista (nombre, tel, RNC, código)'}
            className="w-full rounded-xl border border-background-200/70 bg-background-100/70 backdrop-blur-md pl-9 pr-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400"
          />

          {open && matches.length > 0 && (
            <div className="absolute z-20 mt-1 w-full rounded-lg border border-background-200 bg-background-50 shadow-lg max-h-60 overflow-y-auto">
              {matches.map((c) => (
                <button
                  key={c.id}
                  onClick={() => select(c)}
                  className="w-full text-left px-3 py-2 hover:bg-background-100 cursor-pointer"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-foreground-950">{c.name}</span>
                    {c.is_wholesale && (
                      <span className="inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-accent-100 text-accent-900 whitespace-nowrap">
                        MAYORISTA
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-foreground-500">
                    {[c.wholesale_code, c.rnc, c.phone].filter(Boolean).join(' · ') || 'Cliente'}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={onAdd}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-primary-500 hover:bg-primary-600 px-3.5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
          title="Agregar cliente"
        >
          <i className="ri-add-line text-base" />
          <span className="hidden sm:inline">Cliente</span>
        </button>

        {onDismiss && (
          <button
            onClick={onDismiss}
            className="shrink-0 w-10 h-10 rounded-xl border border-background-200/70 bg-background-100/70 flex items-center justify-center text-foreground-500 hover:bg-background-200/70 cursor-pointer"
            title="Cerrar búsqueda"
          >
            <i className="ri-close-line text-lg" />
          </button>
        )}
      </div>

      {customer && (
        <div
          className={`rounded-lg border px-3 py-2 flex items-center gap-2 ${
            isWholesaleActive
              ? 'border-accent-200 bg-accent-50'
              : 'border-background-200 bg-background-50'
          }`}
        >
          <i
            className={`${
              isWholesaleActive ? 'ri-checkbox-circle-fill text-accent-600' : 'ri-user-3-line text-foreground-400'
            } text-lg`}
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground-950">
              {customer.company || customer.name}
              {customer.company && <span className="text-foreground-500"> · {customer.name}</span>}
            </p>
            <p className="text-xs text-foreground-500">
              {[customer.wholesale_code, customer.rnc ? `RNC ${customer.rnc}` : null, customer.phone]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          {isWholesale ? (
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${
                isWholesaleActive
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-secondary-100 text-secondary-900'
              }`}
            >
              {isWholesaleActive ? 'MAYORISTA' : 'MAYORISTA INACTIVO'}
            </span>
          ) : (
            <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-background-100 text-foreground-500 whitespace-nowrap">
              Cliente normal
            </span>
          )}
          <button
            onClick={() => onChange(null)}
            className="w-6 h-6 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 hover:text-foreground-700 cursor-pointer shrink-0"
            title="Quitar cliente"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      )}
    </div>
  );
}